<template>
    <div class="animated fadeIn">
        <div class="kgr-inputGroup">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Start')"
                        :error="error"
                        field-name="start"
                    >
                        <kgr-date-picker
                            v-model="start"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Ende')"
                        :error="error"
                        field-name="end"
                    >
                        <kgr-date-picker
                            v-model="end"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell kgr-grid__cell--bottom">
                    <button
                        class="kgr-button kgr-button--accent"
                        :disabled="loading"
                        @click="loadCollectors()"
                    >
                        {{ $t('Berechnen') }}
                    </button>
                </div>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div v-if="loading">
            <kgr-loader />
        </div>
        <div v-if="hasData">
            <div class="kgr-row kgr-text kgr-text--rightAligned">
                <button
                    class="kgr-button kgr-button--accent kgr-button--small"
                    @click="toggleAllOpen()"
                >
                    {{ $t('Alles Auf-/Zuklappen') }}
                </button>
            </div>
            <kgr-accordion>
                <kgr-accordion-item
                    v-for="groupedCollector in groupedCollectors"
                    :key="groupedCollector.id"
                    :open="allOpen"
                >
                    <template #header-left>
                        <table class="kgr-table kgr-table--block">
                            <thead>
                                <tr class="kgr-table__row">
                                    <th class="kgr-table__cell kgr-table__cell--headline">
                                        {{ $t('Abteilung(en)') }}
                                    </th>
                                    <th class="kgr-table__cell kgr-table__cell--headline">
                                        {{ $t('Kostenstelle') }}
                                    </th>
                                    <th class="kgr-table__cell kgr-table__cell--headline">
                                        {{ $t('Sachkonto') }}
                                    </th>
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline "
                                        style="text-align:right;"
                                    >
                                        {{ $t('Betrag') }}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr>
                                    <td>
                                        {{ groupedCollector.department }}
                                    </td>
                                    <td>
                                        {{ groupedCollector.costCenter }}
                                    </td>
                                    <td>
                                        {{ groupedCollector.impersonalAccount }}
                                    </td>
                                    <td class="kgr-text--rightAligned">
                                        {{ groupedCollector.amountDisplay }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                    <div
                        style="display:flex; justify-content: right; margin-right: 20px"
                    >
                        <table
                            class="kgr-table"
                            style="width: 80%"
                        >
                            <thead>
                                <tr class="kgr-table__row">
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline"
                                        style="width:60%;"
                                    >
                                        {{ $t('Gebühr') }}
                                    </th>
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline"
                                        style="text-align:right;"
                                    >
                                        {{ $t('Betrag') }}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr
                                    v-for="(voucher, index) in groupedCollector.vouchers"
                                    :key="index + voucher.groupedId"
                                >
                                    <td>
                                        {{ voucher.fee }}
                                    </td>
                                    <td class="kgr-text--rightAligned">
                                        {{ voucher.amountDisplay }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </kgr-accordion-item>
            </kgr-accordion>
            <div class="kgr-row">
            </div>
            <div class="kgr-alert kgr-alert--info kgr-row">
                <h3 class="kgr-headline kgr-headline--h3">
                    {{ $t('Zusammenfassung') }}
                </h3>
                {{ $t('Verteilte Gutschriften:') }} {{ totalMoneyDisplay }}
                <div>
                    {{ $t('davon aus Vereinsbeiträgen verteilt:') }} {{ distributedMoneyDisplay }}
                </div>
                <div>
                    {{ $t('davon direkt auf eigene Kostenstellen gebucht:') }} {{ directlyBookedMoneyDisplay }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format, parse, startOfWeek } from '@legacy/js/date/date-fns.js';
import Accordion from '../../../components/common/accordion/Accordion.vue';
import AccordionItem from '../../../components/common/accordion/AccordionItem.vue';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import Helper from '../../../js/accounting/Helper.js';
import Loading from '../../../components/common/Loading.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-date-picker': DatePicker,
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-loader': Loading,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            allOpen: false,
            vouchers: [],
            groupedCollectors: [],
            expandedCollectors: [],
            loading: false,
            dataLoaded: false,
            start: format(startOfWeek(new Date()), 'dd.MM.yyyy'),
            end: format(new Date(), 'dd.MM.yyyy'),
            filter: {
                start: new Helper().oneWeekAgo(),
                end: new Helper().today(),
            },
        };
    },
    computed: {
        totalMoneyDisplay() {
            const reducer = (accumulator, currentValue) => accumulator + currentValue.amount;
            return this.groupedCollectors.reduce(reducer, 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
        },
        // Beiträge ohne Abteilung durchlaufen die Verteilung (40/30 % Verwaltung, Rest auf die Abteilungen des
        // Zahlers); Beiträge MIT Abteilung werden ungeteilt auf deren eigene Kostenstelle gebucht. Beides steht
        // in derselben Tabelle, was ohne diese Aufschlüsselung nicht zu erkennen ist.
        distributedMoneyDisplay() {
            return this._sumMoneyDisplay((voucher) => !voucher.directlyBooked);
        },
        directlyBookedMoneyDisplay() {
            return this._sumMoneyDisplay((voucher) => voucher.directlyBooked);
        },
        hasData() {
            return !this.loading && this.groupedCollectors.length > 0;
        },
    },
    methods: {
        loadCollectors() {
            this.loading = true;
            const start = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(start, 'yyyy-MM-dd');
            const end = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(end, 'yyyy-MM-dd');
            const that = this;
            axios.get(`/club/clubs/${this.clubId}/fee-distribution?accountingPeriod=${formattedStart}:${formattedEnd}`)
                .then((response) => {
                    that.vouchers = response.data.data.map((voucher) => {
                        return {
                            department: voucher.department,
                            costCenter: voucher.costCenter,
                            impersonalAccount: voucher.impersonalAccount,
                            fee: voucher.fee,
                            amount: voucher.amount,
                            amountDisplay: voucher.amount.toLocaleString('de-DE', { style: 'currency', currency: voucher.currency }),
                            currency: voucher.currency,
                            directlyBooked: voucher.directlyBooked,
                            groupedId: voucher.groupedId,
                        };
                    });
                    that._loadGroupedCollectors();
                })
                .catch((error) => {
                    // axios hands the catch its error object, but Error.fromResponse reads a response
                    // ({status, data}) - as every repository call site does. A network error carries no
                    // response at all, and then there is nothing to parse and the generic toast applies.
                    this.error = error.response ? Error.fromResponse(error.response) : new Error();
                    this._notifyLoadingError(this.error);
                })
                .finally(() => this.loading = false);
        },
        // The distribution refuses to start when a fee is missing its impersonal account or cost center. That
        // is a configuration problem the club admin can fix themselves - but only if we say which fee.
        _notifyLoadingError(error) {
            const feeName = error.messageForField('feeName');

            if (error.forField('impersonalAccount') && feeName) {
                this.$notify.error(
                    'Die Beitragsverteilung kann nicht berechnet werden: Dem Beitrag „{fee}" ist kein Sachkonto zugeordnet.',
                    { fee: feeName },
                );

                return;
            }

            if (error.forField('costCenter') && feeName) {
                this.$notify.error(
                    'Die Beitragsverteilung kann nicht berechnet werden: Der Abteilung „{department}" des Beitrags „{fee}" ist keine Standard-Kostenstelle zugeordnet.',
                    { department: error.messageForField('departmentName'), fee: feeName },
                );

                return;
            }

            this.$notify.error('error.loadingData');
        },
        toggleAllOpen() {
            this.allOpen = !this.allOpen;
        },
        _loadGroupedCollectors() {
            const groupedCollectors = [];
            this.vouchers.forEach((voucher) => {
                let groupedCollector = groupedCollectors.find((groupedCollector) => groupedCollector.id === voucher.groupedId);
                if (undefined === groupedCollector) {
                    groupedCollector = {
                        department: voucher.department,
                        costCenter: voucher.costCenter,
                        impersonalAccount: voucher.impersonalAccount,
                        fee: '',
                        amount: 0,
                        id: voucher.groupedId,
                        currency: voucher.currency,
                        vouchers: [],
                    };
                    groupedCollectors.push(groupedCollector);
                }
                groupedCollector.vouchers.push(voucher);
                groupedCollector.amount += voucher.amount;
            });
            groupedCollectors.map((obj) => {
                obj.amountDisplay = obj.amount.toLocaleString('de-DE', { style: 'currency', currency: obj.currency });
            });
            this.groupedCollectors = groupedCollectors;
            this.loading = false;
            this.dataLoaded = true;
        },
        // Über die ungruppierten vouchers, nicht über groupedCollectors: eine Gruppe fasst (Abteilung,
        // Kostenstelle, Sachkonto) zusammen und kann damit Zeilen beider Strategien enthalten.
        _sumMoneyDisplay(predicate) {
            return this.vouchers
                .filter(predicate)
                .reduce((accumulator, voucher) => accumulator + voucher.amount, 0)
                .toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss">
    @media print {
    footer {
        display: none !important;
    }
}
</style>
